import { useState, useId, useEffect } from 'react';
import styles from './JobListFilterCard.module.scss';
import classNames from 'classnames';
import { UI_Icon } from 'ui/components/1-atoms/Media/Icon/Icon';
import UI_Chevron from 'ui/icons/icon-chevron-down.svg';

export interface JobListFilterOption {
	id: string;
	description: string;
}

export interface JobListFilterCardProps {
	className?: string;
	heading: string;
	options: JobListFilterOption[];
	selectedIds: string[];
	onClick: (selectedValues: string[]) => void;
}

export const UI_JobListFilterCard = ({ className, heading, options, selectedIds: value, onClick }: JobListFilterCardProps) => {
	const [isOpen, setIsOpen] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(BREAKPOINT_MD).matches : false));
	const BREAKPOINT_MD = '(min-width: 768px)';

	useEffect(() => {
		const mq = window.matchMedia(BREAKPOINT_MD);
		setIsOpen(mq.matches);
		const handler = (e: MediaQueryListEvent) => setIsOpen(e.matches);
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, []);

	const id = useId();
	const listId = `${id}-list`;
	const headingId = `${id}-heading`;

	const handleClick = (optionId: string) => {
		const isSelected = value.includes(optionId);
		const newSelected = isSelected ? value.filter((v) => v !== optionId) : [...value, optionId];
		onClick(newSelected);
	};

	return (
		<div className={classNames(styles.JobListFilterCard, className)}>
			<button className={styles.JobListFilterCard_header} onClick={() => setIsOpen((o) => !o)} aria-expanded={isOpen} aria-controls={listId}>
				<span className={styles.JobListFilterCard_heading} id={headingId}>
					{heading}
				</span>
				<UI_Icon className={classNames(styles.JobListFilterCard_chevron, isOpen && styles.JobListFilterCard_chevron___open)} size="sm">
					<UI_Chevron />
				</UI_Icon>
			</button>
			<ul
				id={listId}
				className={classNames(styles.JobListFilterCard_list, !isOpen && styles.JobListFilterCard_list___collapsed)}
				role="group"
				aria-labelledby={headingId}
			>
				{options.map((option) => {
					const isChecked = value.includes(option.id);
					const id = `job-filter-${option.id}`;
					return (
						<li key={option.id}>
							<label className={styles.JobListFilterCard_label} htmlFor={id}>
								<input
									id={id}
									type="checkbox"
									checked={isChecked}
									onChange={() => handleClick(option.id)}
									className={styles.JobListFilterCard_checkbox}
								/>
								{option.description}
							</label>
						</li>
					);
				})}
			</ul>
		</div>
	);
};
